import React, { useCallback, useEffect, useState } from 'react';
import { css } from '@emotion/css';
import Cropper, { Area, Point, Size } from 'react-easy-crop';
import { camelKeys } from 'js-convert-case';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Box from '@prezzee/ribbon-ui/lib/components/Box';
import Button from '@prezzee/ribbon-ui/lib/components/Button';
import colors from '@prezzee/ribbon-ui/lib/theme/foundations/colors';
import Container from '@prezzee/ribbon-ui/lib/components/Container';
import {
  Modal,
  ModalOverlay,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  ModalContent,
} from '@prezzee/ribbon-ui/lib/components/Modal';
import { Slider, SliderTrack, SliderFilledTrack, SliderThumb } from '@prezzee/ribbon-ui/lib/components/Slider';
import { HStack } from '@prezzee/ribbon-ui/lib/components/Stack';
import Typography from '@prezzee/ribbon-ui/lib/components/Typography';

import { useProduct } from 'contexts/ProductPageContext';
import useCustomCardDesign from 'hooks/useCustomCardDesign';
import { CustomThemeDesign } from 'models/sku.model';
import { CustomThemeDesign as CustomThemeDesignResult } from 'models/Models';
import { ActionType } from 'reducers/CardCustomisationReducer';
import logger from 'utils/logger';

const CARD_CROP_SIZE: Size = {
  width: 452,
  height: 280,
};
const DESIGN_UPLOAD_API = process.env.NEXT_PUBLIC_PREZZEE_SERVER_URL + 'api/v3/designs/';
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 3;
const ZOOM_STEP_INCREMENT = 0.1;

const CustomCardDesign = (): JSX.Element => {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [cropperModalIsVisible, setCropperModalIsVisible] = useState<boolean>(false);
  const [generateImg, setGenerateImg] = useState(false);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [croppedImage, setCroppedImage] = useState<Blob | null>(null);

  const { generateImageBlob, getImageFileDataUrl, uploadImage } = useCustomCardDesign();
  const { cardCustomisationState, dispatchCardCustomisationAction, userUploadedDesignFileInput } = useProduct();

  const { uid } = cardCustomisationState.selectedCardDesign;

  const incrementZoom = () => {
    const nextStep = zoom + ZOOM_STEP_INCREMENT;
    setZoom(Math.min(nextStep, MAX_ZOOM));
  };

  const decrementZoom = () => {
    const prevStep = zoom - ZOOM_STEP_INCREMENT;
    setZoom(Math.max(prevStep, MIN_ZOOM));
  };

  const reset = () => {
    if (zoom !== 1 || crop.x !== 0 || crop.y !== 0) {
      setZoom(1);
      setCrop({ x: 0, y: 0 });
    }
  };

  const onFileChange = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    ev.preventDefault();
    if (ev.target.files?.length > 0) {
      const file = ev.target.files[0];
      const imageDataUrl = getImageFileDataUrl(file);
      setImageSrc(imageDataUrl?.encodeImageFileAsURL ?? '');
      setCropperModalIsVisible(true);
    }
  };

  const handleCancel = (ev?: React.MouseEvent<HTMLElement>) => {
    if (ev) {
      ev.preventDefault();
    }
    setCropperModalIsVisible(false);
    setCroppedImage(null);
    reset();
  };

  const handleSuccess = (result: CustomThemeDesignResult) => {
    const customThemeDesign = camelKeys(result, {
      recursive: true,
      recursiveInArray: true,
    }) as CustomThemeDesign;
    dispatchCardCustomisationAction({
      type: ActionType.CreateCustomThemeDesign,
      payload: {
        templateUid: uid,
        customThemeDesign,
      },
    });
    reset();
    setCropperModalIsVisible(false);
  };

  //set area
  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const onCropped = React.useCallback(
    async (croppedImg: Blob) => {
      if (croppedImage) {
        try {
          const result = await uploadImage(croppedImg, uid, DESIGN_UPLOAD_API);
          if (result.ok) {
            const json = await result.json();
            setGenerateImg(false);
            handleSuccess(json);
            return;
          }
          await result.json();
          setGenerateImg(false);
        } catch (e) {
          logger.info({
            message: 'info: could not generate image with Custom Theme Cropper',
            error: e,
          });
          setGenerateImg(false);
        }
      }
    },
    [generateImg, handleSuccess, uid, DESIGN_UPLOAD_API]
  );

  const showCroppedImage = useCallback(async () => {
    setGenerateImg(true);
    try {
      const croppedImg = await generateImageBlob({ imageSrc, pixelCrop: croppedAreaPixels });
      setCroppedImage(croppedImg);
    } catch (e) {
      logger.warn({
        message: 'could not crop image',
        error: e,
      });
    }
  }, [croppedAreaPixels]);

  useEffect(() => {
    onCropped(croppedImage);
  }, [croppedImage]);

  if (!cardCustomisationState?.selectedCardDesign) return null;

  return (
    <React.Fragment>
      <Modal isOpen={!!(cropperModalIsVisible && uid && imageSrc)} size="4xl" onClose={() => handleCancel()}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader
            textAlign="center"
            fontSize={{ sm: '2xl', base: 'lg' }}
            borderBottom="1px"
            borderBottomColor={colors.gray[200]}
            px="24"
          >
            Resize &amp; Crop
          </ModalHeader>
          <ModalCloseButton
            top={{ sm: '5', base: '4' }}
            right="6"
            w={{ sm: '8', base: '7' }}
            h={{ sm: '8', base: '7' }}
          />
          <Button
            variant="link"
            type="button"
            position="absolute"
            left="6"
            top={{ sm: '6', base: '5' }}
            color={colors.notification.blue}
            onClick={() => reset()}
          >
            <FontAwesomeIcon icon={['far', 'undo']} size="xs" />
            <Typography ml="1" variant="subtitle3" as="span" mb="2px">
              Refresh
            </Typography>
          </Button>
          <ModalBody py="0" px="5">
            <Container className={Styles.cropperContainer} w="100%" maxW="100%">
              <Cropper
                aspect={CARD_CROP_SIZE.width / CARD_CROP_SIZE.height}
                crop={crop}
                // cropSize={CARD_CROP_SIZE}
                image={imageSrc}
                minZoom={0.1}
                onCropComplete={onCropComplete}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                restrictPosition={true}
                showGrid={false}
                zoom={zoom}
                zoomWithScroll={false}
              />
            </Container>
          </ModalBody>
          <ModalFooter justifyContent="center" px="5" py="4" borderTop="1px" borderTopColor={colors.gray[200]}>
            <HStack w="100%" wrap={{ sm: 'nowrap', base: 'wrap' }} alignItems="center">
              <Box pl="0" w="100%" mb={{ sm: '0', base: '3' }}>
                <HStack alignContent="center" spacing="4">
                  <Button
                    variant="tertiary"
                    type="button"
                    onClick={() => decrementZoom()}
                    className={Styles.stepButton}
                    color={colors.red[400]}
                  >
                    <FontAwesomeIcon icon={['fas', 'minus']} />
                  </Button>
                  <Box w="100%">
                    <Slider
                      aria-label="slider-zoom"
                      min={0.1}
                      max={3}
                      step={0.01}
                      value={zoom}
                      onChange={value => setZoom(value)}
                      display="block"
                    >
                      <SliderTrack height={1}>
                        <SliderFilledTrack />
                      </SliderTrack>
                      <SliderThumb
                        boxSize={7}
                        border="3px"
                        borderStyle="solid"
                        borderColor={colors.red[400]}
                        backgroundColor="white"
                      />
                    </Slider>
                  </Box>
                  <Button
                    variant="tertiary"
                    type="button"
                    onClick={() => incrementZoom()}
                    className={Styles.stepButton}
                    color={colors.red[400]}
                  >
                    <FontAwesomeIcon icon={['fas', 'plus']} />
                  </Button>
                </HStack>
              </Box>
              <Box width={{ sm: '156px', base: '100%' }} className={Styles.submitButtonWrapper}>
                <Button isDisabled={generateImg} onClick={() => showCroppedImage()} fullWidth={true}>
                  {generateImg ? 'Uploading...' : 'Crop & Save'}
                </Button>
              </Box>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <input
        value=""
        type="file"
        id="file"
        ref={userUploadedDesignFileInput}
        accept="image/png, image/jpeg"
        onChange={onFileChange}
        className={Styles.fileInput}
      />
    </React.Fragment>
  );
};

const Styles = {
  cropperContainer: css`
    position: relative;
    cursor: crosshair;
    overflow: hidden;
    max-width: 100%;
    padding-top: ${Math.round((CARD_CROP_SIZE.height / CARD_CROP_SIZE.width) * 100)}%;

    .reactEasyCrop_CropArea {
      box-shadow: 0 0 9999em rgb(255, 255, 255);
      border-color: ${colors.gray[200]};
    }
  `,
  submitButtonWrapper: css`
    @media (max-width: 424px) {
      margin-left: 0 !important;
    }
  `,
  stepButton: css`
    &:hover {
      background-color: transparent !important;
    }
  `,
  fileInput: css`
    display: none;
  `,
};

export default CustomCardDesign;
